import "./print.css";
import React from "react";
import { Schedule, displayTime, displayInstant, paperRoomLabel, roomGroupOrder } from "./api";
import { CleaningLevels } from "./CleaningLevels";
const roomThai: Record<string, string> = {
  "Standard Room": "ห้องสแตนดาร์ด",
  "Standard Villa": "วิลล่าสแตนดาร์ด",
  "Superior Villa": "วิลล่าซูพีเรีย",
  "Single Room": "ห้องเดี่ยว",
};
const PAGE_ROWS_MM = 200;
// Greedy word wrap, as the browser breaks a cell: a word past the width
// starts a new line, and a word wider than the line takes as many as it needs.
function wrappedLines(text: string, charsPerLine: number): number {
  let lines = 0,
    width = 0;
  for (const word of text.split(/\s+/).filter(Boolean)) {
    if (width && width + 1 + word.length <= charsPerLine) width += 1 + word.length;
    else {
      lines += Math.ceil(word.length / charsPerLine);
      width = word.length % charsPerLine || charsPerLine;
    }
  }
  return lines;
}
const ROW_PADDING_MM = 1.6;
const TASK_LINE_MM = 4.5;
export function PrintSheet({
  data,
  preview = false,
}: {
  data: Schedule;
  preview?: boolean;
}) {
  const allRooms = roomGroupOrder(data.rooms)
    .map((room) => ({
      ...room,
      roomLabel: paperRoomLabel(room.roomLabel),
      day: room.days.find((day) => day.date === data.from),
    }))
    .filter((room) => room.day);
  const rooms = allRooms
    .map((room) => ({
      ...room,
      day: {
        ...room.day!,
        tasks: room.day!.tasks.filter(
          (task) => task.state !== "superseded",
        ),
      },
    }))
    .filter((room) => room.day.tasks.length > 0);
  const occupied = allRooms.filter(
    (room) => room.day!.occupancy === "occupied",
  ).length;
  const arrivals = allRooms.filter((room) =>
    ["arrival", "turnaround"].includes(room.day!.occupancy),
  ).length;
  const departures = allRooms.filter((room) =>
    ["departure", "turnaround"].includes(room.day!.occupancy),
  ).length;
  // Paginate by estimated printed height so a full 23-room day with one clean
  // per room fits page one; the budget is A4 minus header, summary, headings,
  // supervisor box and footer, and each row is its tallest column in mm.
  const pages: (typeof rooms)[] = [[]];
  let usedMm = 0;
  for (const room of rooms) {
    const heightMm = ROW_PADDING_MM + Math.max(
      wrappedLines(room.roomLabel, 10) * 4.5,
      (wrappedLines(room.groupLabel, 24) + (roomThai[room.groupLabel] ? 1 : 0)) * 3,
      wrappedLines(room.day.guestNames.join(", "), 24) * 3.6 +
        (Number(!!room.day.checkInAt) + Number(!!room.day.checkOutAt)) * 2.8,
      room.day.tasks.length * TASK_LINE_MM,
    );
    if (usedMm + heightMm > PAGE_ROWS_MM && pages[pages.length - 1].length) {
      pages.push([]);
      usedMm = 0;
    }
    pages[pages.length - 1].push(room);
    usedMm += heightMm;
  }
  const date = new Date(`${data.from}T12:00:00Z`);
  return (
    <div className={`hk-print-only ${preview ? "hk-print-preview" : ""}`}>
      {pages.map((page, pageIndex) => (
        <section className="hk-print-page" key={pageIndex}>
          <header className="hk-paper-header">
            <h1>
              Housekeeping schedule<span>ตารางงานแม่บ้าน</span>
            </h1>
            <div>
              {new Intl.DateTimeFormat("en-GB", {
                dateStyle: "full",
                timeZone: data.timezone,
              }).format(date)}
              <span>
                {new Intl.DateTimeFormat("th-TH", {
                  dateStyle: "full",
                  timeZone: data.timezone,
                }).format(date)}
              </span>
            </div>
          </header>
          <p className="hk-paper-summary">
            {occupied} rooms occupied · {arrivals} arrivals · {departures}{" "}
            departures
            <br />
            มีแขกพัก {occupied} ห้อง · มีแขกเข้า {arrivals} ห้อง · มีแขกออก{" "}
            {departures} ห้อง
            <br />
            Today's work: ✓ done, ✗ declined / งานวันนี้: ✓ เสร็จ, ✗ แขกปฏิเสธ
          </p>
          <table className="hk-paper-table">
            <colgroup>
              {[12, 20, 23, 6, 21, 8, 10].map((width, index) => (
                <col key={index} style={{ width: `${width}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th>
                  Room
                  <br />
                  ห้อง
                </th>
                <th>
                  Type
                  <br />
                  ประเภท
                </th>
                <th>
                  Guest
                  <br />
                  ชื่อแขก
                </th>
                <th>
                  Day
                  <br />
                  วันที่
                </th>
                <th>
                  Clean
                  <br />
                  ระดับความสะอาด
                </th>
                <th>
                  Done
                  <br />
                  เสร็จ
                </th>
                <th>
                  Initials
                  <br />
                  ลงชื่อ
                </th>
              </tr>
            </thead>
            <tbody>
              {page.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    No outstanding housekeeping work / ไม่มีงานแม่บ้านค้าง
                  </td>
                </tr>
              )}
              {page.map((room, index) => (
                <tr key={room.roomId}>
                  <th>{room.roomLabel}</th>
                  <td>
                    {room.groupLabel}
                    <br />
                    {roomThai[room.groupLabel] || ""}
                  </td>
                  <td>{room.day!.guestNames.join(", ")}
                    {room.day.checkInAt && <div className="hk-booking-time">Check-in {displayTime(room.day.checkInAt, data.timezone)}</div>}
                    {room.day.checkOutAt && <div className="hk-booking-time">Check-out {displayTime(room.day.checkOutAt, data.timezone)}</div>}
                  </td>
                  <td>{room.day!.stayDay ?? ""}</td>
                  <td>
                    {room
                      .day!.tasks.filter((task) => task.state !== "superseded")
                      .map((task) => (
                        <span className="hk-paper-code" key={task.taskId}>
                          {task.code}
                        </span>
                      ))}
                  </td>
                  <td>
                    {room.day.tasks.map((task) => (
                      <div className="hk-paper-task-result" key={task.taskId}>
                        {task.state === "completed" || task.state === "declined" ? (
                          <span className="hk-paper-mark">
                            {task.state === "completed" ? "✓" : "✗"}
                          </span>
                        ) : (
                          <span className="hk-paper-checkbox" />
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    {room.day.tasks.map((task) => (
                      <div className="hk-paper-task-result" key={task.taskId}>
                        {task.state === "completed" || task.state === "declined"
                          ? task.lastActionStaffInitials || "\u00a0"
                          : "________"}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <aside className="hk-paper-supervisor">
            <strong>Supervisor check / หมายเหตุสำหรับหัวหน้าแม่บ้าน</strong>
            <p>
              ________________________________________________________________________________
            </p>
          </aside>
          <footer>
            Generated {displayInstant(data.asOf, data.timezone)} · Sheet
            reference: {data.from} / room label · {pageIndex + 1} /{" "}
            {pages.length + 1}
          </footer>
        </section>
      ))}
      <section className="hk-print-page">
        <h1 className="hk-paper-level-title">
          Cleaning levels / ระดับการทำความสะอาด
        </h1>
        <CleaningLevels rules={data.rules} />
        <div className="hk-paper-signatures">
          <span>
            Issued by / ผู้จัดตาราง
            <br />
            ______________________
          </span>
          <span>
            Checked by / ผู้ตรวจ
            <br />
            ______________________
          </span>
          <span>
            Time / เวลา
            <br />
            ______________________
          </span>
        </div>
        <footer>
          Generated {displayInstant(data.asOf, data.timezone)} ·{" "}
          {pages.length + 1} / {pages.length + 1}
        </footer>
      </section>
    </div>
  );
}
